import React, { useState, useEffect, useRef } from "react";
import GTM from "@utils/GTM";
import styled from "styled-components";
import "./style.css";

const StyledVimeoContainer = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

/**
 * Vimeo Video Component
 * 
 * @param {Object} props - Component props
 * @param {string} props.videoId - Vimeo video ID
 * @param {string} props.videoHash - Vimeo video hash (optional)
 * @param {string} props.videoTitle - Title of the video
 * @param {boolean} props.autoplay - Whether to autoplay the video when loaded
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.thumbnail - Thumbnail image object
 * @returns {JSX.Element} - Rendered component
 */
const Vimeo = ({
	videoId,
	videoHash,
	videoTitle = "Video",
	autoplay = false,
	className = "",
	playerType = "Vimeo",
	playMethod = "Inline",
	thumbnail,
  ...rest
}) => {
	const [videoPlaying, setVideoPlaying] = useState(autoplay);
	const [hidePlayButton, setHidePlayButton] = useState(autoplay);
  const videoWrapperRef = useRef(null);

	useEffect(() => {
		if (videoPlaying) {
			setHidePlayButton(true);
			// Track video start when it begins playing
			GTM.videoStart(videoTitle);
		} else {
			setHidePlayButton(false);
		}
	}, [videoPlaying, videoTitle]);

	// Function to construct a Vimeo URL from videoId
	const getVimeoUrl = (id, hash) => {
		if (!id) return null;

		// Return direct embed URL with parameters
		return `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}&` : '?'}title=0&byline=0&portrait=0&badge=0&autopause=0${autoplay || videoPlaying ? '&autoplay=1' : ''}`;
	};

	const playVideo = () => {
		setVideoPlaying(true);
		if (videoWrapperRef.current) {
			videoWrapperRef.current.focus();
		}
	};


  return (
	  <StyledVimeoContainer
		  className={`vimeo-container ${className}`}
		  {...rest}
	  >
		  <div tabIndex={-1} ref={videoWrapperRef} className="video-wrapper">
			  {!hidePlayButton && (
				  <button
					  data-id="play-video"
					  onClick={playVideo}
					  className={`video-play-button watch-video ${hidePlayButton ? "video-play-button--hidden" : ""}`}
					  aria-label={`Play ${videoTitle}`}
				  >
					  {thumbnail && thumbnail.file && thumbnail.file.url && (
						  <picture className="thumbnail-image">
							  <source srcSet={thumbnail.file.url} />
							  <img src={thumbnail.file.url} alt={videoTitle} />
						  </picture>
					  )}

				  </button>
			  )}
			  <div
				  className={`video-player-wrapper ${hidePlayButton ? "video-player-wrapper--visible" : ""}`}
			  >
				  {hidePlayButton && (
					  <iframe
						  src={getVimeoUrl(videoId, videoHash)}
						  title={videoTitle}
						  width="100%"
						  height="100%"
						  frameBorder="0"
						  allow="autoplay; fullscreen; picture-in-picture"
						  allowFullScreen
						  className="w-full h-full"
						  onEnded={() => {
							  setVideoPlaying(false);
							  GTM.videoEnd(videoTitle);
						  }}
					  ></iframe>
				  )}
			  </div>
		  </div>
	  </StyledVimeoContainer>
  );
};

export default Vimeo;
